import { CHANGE_MOCK_CURRENT_DATE, CHANGE_STATUS, MY_CHANGE_OWNERS, OPEN_CHANGE_STATUSES } from './constants';
import type { TChangeDashboardTab, TChangeMockConfig, TChangeMockEntry } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

export const isChangeMockVisible = (config: TChangeMockConfig | undefined): boolean => Boolean(config?.changes?.length);

export const getChangeDashboardPath = (productId?: string): string =>
  productId ? `/product/${productId}/changes` : '/changes';

export const getChangeCreatePath = (productId?: string): string =>
  productId ? `/product/${productId}/changes/create` : '/changes/create';

export const getChangeDetailPath = (changeNumber: string, productId?: string): string =>
  productId ? `/product/${productId}/changes/${changeNumber}` : `/changes/${changeNumber}`;

export const getNextChangeNumber = (changes: TChangeMockEntry[]): string => {
  const year = CHANGE_MOCK_CURRENT_DATE.getUTCFullYear();
  const nextSequence =
    changes.reduce((highestSequence, change) => {
      const match = change.changeNumber.match(/^CHG-(\d{4})-(\d+)$/);
      if (!match || Number(match[1]) !== year) return highestSequence;
      return Math.max(highestSequence, Number(match[2]));
    }, 0) + 1;

  return `CHG-${year}-${String(nextSequence).padStart(4, '0')}`;
};

export const toSentenceCaseLabel = (value: string): string => {
  const words = value.split(' ');
  return words
    .map((word, index) => {
      if (index === 0) return word;
      if (word === word.toUpperCase()) return word;
      return word.toLowerCase();
    })
    .join(' ');
};

export const getFilterOptionLabel = (value: string, emptyLabel: string): string =>
  value ? toSentenceCaseLabel(value) : emptyLabel;

export const parseMockDate = (value: string): Date => new Date(`${value}T00:00:00.000Z`);

export const getTargetDateOffsetDays = (targetDate: string): number =>
  Math.round((parseMockDate(targetDate).getTime() - CHANGE_MOCK_CURRENT_DATE.getTime()) / DAY_MS);

const isSameStatus = (status: string, expectedStatus: string): boolean =>
  status.toLocaleLowerCase() === expectedStatus.toLocaleLowerCase();

const isStatusIn = (status: string, expectedStatuses: Iterable<string>): boolean =>
  Array.from(expectedStatuses).some((expectedStatus) => isSameStatus(status, expectedStatus));

export const isOpenChange = (change: TChangeMockEntry): boolean => isStatusIn(change.status, OPEN_CHANGE_STATUSES);

export const isDueWithin30Days = (change: TChangeMockEntry): boolean => {
  const offsetDays = getTargetDateOffsetDays(change.targetDate);
  return isOpenChange(change) && offsetDays >= 0 && offsetDays <= 30;
};

export const isOverdue = (change: TChangeMockEntry): boolean =>
  isOpenChange(change) && parseMockDate(change.targetDate) < CHANGE_MOCK_CURRENT_DATE;

export const isCompletedThisYear = (change: TChangeMockEntry): boolean =>
  isStatusIn(change.status, [CHANGE_STATUS.IMPLEMENTED, CHANGE_STATUS.CLOSED]) &&
  parseMockDate(change.targetDate).getUTCFullYear() === CHANGE_MOCK_CURRENT_DATE.getUTCFullYear();

export const getChangeRelativeTargetLabel = (change: TChangeMockEntry): string => {
  const offsetDays = getTargetDateOffsetDays(change.targetDate);

  if (isSameStatus(change.status, CHANGE_STATUS.CLOSED)) return 'Completed this year';
  if (isSameStatus(change.status, CHANGE_STATUS.IMPLEMENTED))
    return offsetDays < 0 ? `Implemented ${Math.abs(offsetDays)} days ago` : 'Implemented';
  if (offsetDays === 0) return 'Due today';
  if (offsetDays > 0) return `Due in ${offsetDays} days`;
  return `${Math.abs(offsetDays)} days overdue`;
};

export const formatMockDate = (value: string): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseMockDate(value));

export const sortChangeMockEntries = (changes: TChangeMockEntry[]): TChangeMockEntry[] =>
  [...changes].sort((a, b) => b.changeNumber.localeCompare(a.changeNumber));

export const getChangeTabCounts = (changes: TChangeMockEntry[]): Record<TChangeDashboardTab, number> => ({
  all: changes.length,
  my: changes.filter((change) => MY_CHANGE_OWNERS.has(change.owner)).length,
  'pending-review': changes.filter((change) => isSameStatus(change.status, CHANGE_STATUS.PENDING_REVIEW)).length,
  'in-execution': changes.filter((change) => isSameStatus(change.status, CHANGE_STATUS.IN_EXECUTION)).length,
  implemented: changes.filter((change) => isSameStatus(change.status, CHANGE_STATUS.IMPLEMENTED)).length,
  closed: changes.filter((change) => isSameStatus(change.status, CHANGE_STATUS.CLOSED)).length,
});

export const getChangeSummary = (changes: TChangeMockEntry[]) => ({
  open: changes.filter(isOpenChange).length,
  awaitingMyReview: changes.filter((change) => change.status === CHANGE_STATUS.PENDING_REVIEW).length,
  dueWithin30Days: changes.filter(isDueWithin30Days).length,
  overdue: changes.filter(isOverdue).length,
});

const matchesTab = (change: TChangeMockEntry, selectedTab: TChangeDashboardTab): boolean => {
  switch (selectedTab) {
    case 'my':
      return MY_CHANGE_OWNERS.has(change.owner);
    case 'pending-review':
      return isSameStatus(change.status, CHANGE_STATUS.PENDING_REVIEW);
    case 'in-execution':
      return isSameStatus(change.status, CHANGE_STATUS.IN_EXECUTION);
    case 'implemented':
      return isSameStatus(change.status, CHANGE_STATUS.IMPLEMENTED);
    case 'closed':
      return isSameStatus(change.status, CHANGE_STATUS.CLOSED);
    default:
      return true;
  }
};

const matchesTargetDate = (change: TChangeMockEntry, targetDateFilter: string): boolean => {
  switch (targetDateFilter) {
    case 'Due within 30 days':
      return isDueWithin30Days(change);
    case 'Overdue':
      return isOverdue(change);
    case 'Completed this year':
      return isCompletedThisYear(change);
    default:
      return true;
  }
};

export const filterChangeMockEntries = (
  changes: TChangeMockEntry[],
  filters: {
    selectedTab: TChangeDashboardTab;
    search: string;
    changeType: string;
    owner: string;
    status: string;
    targetDate: string;
  }
): TChangeMockEntry[] => {
  const normalizedSearch = filters.search.trim().toLocaleLowerCase();

  return changes.filter((change) => {
    if (!matchesTab(change, filters.selectedTab)) return false;
    if (filters.changeType && change.changeType !== filters.changeType) return false;
    if (filters.owner && change.owner !== filters.owner) return false;
    if (filters.status && !isSameStatus(change.status, filters.status)) return false;
    if (!matchesTargetDate(change, filters.targetDate)) return false;
    if (!normalizedSearch) return true;

    return [change.changeNumber, change.changeTitle, change.owner, change.changeType].some((value) =>
      value.toLocaleLowerCase().includes(normalizedSearch)
    );
  });
};
